import { yupResolver } from '@hookform/resolvers/yup';
import usePrevious from 'hooks/usePrevious';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getUserData } from 'store/selectors/user';
import {
  useCreateFeedbackQuestionMutation,
  useGetFeedbackGroupQuery,
  useUpdateFeedbackGroupMutation,
} from 'store/services/feedbackGroup';
import Button from 'style-guide/Button';
import { Plus, Trash } from 'style-guide/Icons';
import { Input } from 'style-guide/Input';
import { message } from 'style-guide/ToastMessages';
import Range from 'views/LearningCommunity/Categories/FeedbackQuestion/Range';
import schema from 'views/LearningCommunity/Categories/FeedbackQuestion/schema';
import { CreateSurveySkeleton } from 'views/LearningCommunity/Categories/FeedbackQuestion/SurveySkeleton';

const formDefaultValues = {
  type: '',
  question: '',
  questionAnswers: [
    { rangeFrom: '', rangeTo: '', answer: '' },
    { rangeFrom: '', rangeTo: '', answer: '' },
  ],
};
const Feedback = ({ setSurveyState, surveyState }) => {
  const {
    control,
    watch,
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      questions: [formDefaultValues],
    },
  });

  const [searchParams] = useSearchParams();
  const [feedbackGroupId, setFeedbackGroupId] = useState(null);
  const user = useSelector(getUserData);
  const previousSurvey = usePrevious(searchParams.get('feedbackGroupId'));

  const { data, isLoading } = useGetFeedbackGroupQuery(
    { groupId: feedbackGroupId, userId: user.id },
    { skip: !feedbackGroupId || !user?.id }
  );
  const [createFeedbackQuestion, { isSuccess, isError }] = useCreateFeedbackQuestionMutation();
  const [updateFeedbackQuestion, { isSuccess: isUpdateSuccess, isError: isUpdateError }] =
    useUpdateFeedbackGroupMutation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions',
  });
  // Update local state when the URL param becomes available
  useEffect(() => {
    const id = searchParams.get('feedbackGroupId');
    if (id) {
      setFeedbackGroupId(id);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      message.success(`Survey successfully has been ${isUpdateSuccess ? 'updated' : 'created'}`);
    }
    if (isError || isUpdateError) {
      message.error('Something went wrong!');
    }
  }, [isSuccess, isError, isUpdateSuccess, isUpdateError]);

  useEffect(() => {
    const id = searchParams.get('feedbackGroupId');
    const subscription = watch((value) => {
      if (isDirty) {
        setSurveyState((prev) =>
          prev?.map((item) =>
            item.id === Number(id) ? { ...item, isValid, isDirty, feedbackQuestions: value.questions } : item
          )
        );
      }
    });

    return () => subscription.unsubscribe();
  }, [searchParams, setSurveyState, watch, isDirty, isValid]);

  useEffect(() => {
    const id = searchParams.get('feedbackGroupId');
    const currentSurvey = surveyState?.find((survey) => survey.id === Number(id));

    if (previousSurvey !== id && currentSurvey?.id === Number(id)) {
      reset({
        questions:
          currentSurvey?.feedbackQuestions && currentSurvey?.feedbackQuestions.length > 0
            ? currentSurvey.feedbackQuestions.map((item) => ({
                id: item.id,
                type: item.type,
                question: item.question,
                questionAnswers: item.questionAnswers.map((answer) => ({
                  rangeId: answer.id,
                  rangeFrom: answer.rangeFrom,
                  rangeTo: answer.rangeTo,
                  answer: answer.answer,
                })),
              }))
            : [formDefaultValues],
      });
    }
  }, [previousSurvey, reset, searchParams, surveyState]);

  const onFormSubmit = (formData) => {
    const { questions } = formData;
    if (data?.feedbackQuestions.length) {
      updateFeedbackQuestion({ id: data?.id, questions });
      return;
    }
    createFeedbackQuestion({ questions, feedbackGroupId });
  };

  return (
    <>
      <Button
        variant='link'
        prefix={<Plus />}
        className='mb--40'
        onClick={() => append(formDefaultValues)}
        disabled={fields.length >= 5}
      >
        Add Question
      </Button>
      {isLoading ? (
        <CreateSurveySkeleton />
      ) : (
        fields.map((field, index) => (
          <div key={field.id} className='mb--16'>
            <div className='d-flex align-items-end'>
              <Input
                className='question-type mr--16'
                label='Type'
                {...register(`questions.${index}.type`)}
                required
                error={errors?.questions?.[index]?.type}
              />
              <Input
                className='flex-1'
                placeholder='Write Question'
                {...register(`questions.${index}.question`)}
                required
                error={errors?.questions?.[index]?.question}
              />
              {fields.length > 1 && (
                <Button variant='text' $danger onClick={() => remove(index)} className='mb--24 ml--8'>
                  <Trash />
                </Button>
              )}
            </div>
            <Range
              index={index}
              control={control}
              register={register}
              question='questions'
              errors={errors}
              trigger={trigger}
            />
          </div>
        ))
      )}
      <div className='d-flex justify-content-end mb--16'>
        <Button variant='primary' onClick={handleSubmit(onFormSubmit)} disabled={!isValid || !isDirty}>
          {data?.feedbackQuestions.length ? 'Update' : 'Save'}
        </Button>
      </div>
    </>
  );
};
export default Feedback;
